import numeral from 'numeral';

import InfoIcon from '@/assets/svg/ui-elements/info-circle-outline-gray.svg';
import { Tooltip, Typography } from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import {
  formatDefaultFractionTwo,
  formatNumberWithLetter,
  splitAmountParts,
} from '@/utils/numbers/formatters';

import { ChartPeriodType } from '../../common';
import { PerformanceChartCard } from './performance-chart-card';
import { HeadingInfo, HeadingRow, PerformanceLayout } from './portfolio-performance.styled';
import { PortfolioSummary } from './portfolio-summary';

export interface PerformanceData {
  data: Array<{ date: string; value: number }>;
  numberOfPositions: number;
  totalInvestedInUsd: number | null;
  totalBalanceInUsd: number | null;
  totalPnlInUsd: number | null;
}

export interface PortfolioPerformanceProps {
  data: PerformanceData | undefined;
  isLoading: boolean;
  activeAssetName?: string;
  activePeriod: ChartPeriodType;
  onPeriodChange: (_period: ChartPeriodType) => void;
}

export const PortfolioPerformance = ({
  data,
  isLoading,
  activeAssetName,
  activePeriod,
  onPeriodChange,
}: PortfolioPerformanceProps) => {
  const isMobile = useIsMobile();
  const performances = data?.data ?? [];
  const hasPerformances = performances.length > 0;
  const hasChartData = performances.length >= 2;
  const firstValue = hasPerformances ? performances[0].value : 0;
  const lastValue = hasPerformances ? performances[performances.length - 1].value : 0;
  const delta = lastValue - firstValue;
  const percent = hasPerformances && firstValue !== 0 ? delta / firstValue : 0;
  const heading = activeAssetName ? `Performance: ${activeAssetName}` : 'Performance';
  const formatSummaryCurrency = (value: number | null | undefined) => {
    if (value == null) return '—';
    return isMobile ? `$${formatDefaultFractionTwo(value)}` : `$${formatNumberWithLetter(value)}`;
  };
  const summary = [
    { label: 'Total Balance', value: formatSummaryCurrency(data?.totalBalanceInUsd) },
    { label: 'My PnL', value: formatSummaryCurrency(data?.totalPnlInUsd) },
    { label: 'Assets', value: data?.numberOfPositions ?? 0 },
    { label: 'Total Invested', value: formatSummaryCurrency(data?.totalInvestedInUsd) },
  ];
  const balance = splitAmountParts(data?.totalBalanceInUsd);
  const balanceDisplayValue =
    data?.totalBalanceInUsd == null ? '—' : formatDefaultFractionTwo(data.totalBalanceInUsd);
  const performanceChange = hasPerformances
    ? { percent: numeral(percent).format('0.00%'), isPositive: delta >= 0 }
    : undefined;

  return (
    <section>
      <HeadingRow>
        <Typography as="h2" variant={isMobile ? 'h5' : 'h4'} fontWeight={500} title={heading}>
          {heading}
        </Typography>
        <Tooltip title="Portfolio value over the selected period, based on your current holdings.">
          <HeadingInfo>
            <InfoIcon />
          </HeadingInfo>
        </Tooltip>
      </HeadingRow>

      <PerformanceLayout>
        <PerformanceChartCard
          isMobile={isMobile}
          balance={balance}
          balanceDisplayValue={balanceDisplayValue}
          performanceChange={performanceChange}
          performanceData={performances}
          hasChartData={hasChartData}
          isLoading={isLoading}
          activePeriod={activePeriod}
          onPeriodChange={onPeriodChange}
        />

        <PortfolioSummary summary={summary} />
      </PerformanceLayout>
    </section>
  );
};
